// Entry point: mounts every [data-mcc-app] on the page.
//   hub | builder | search | competitive | archetype | list | explorer | pdp-additions | requests
// The singles collection page renders one mount ("hub"); URL params switch it to the builder (?deck / ?list / ?c)
// or search (?q / ?screen=search / filter params). ?card=<id> opens the card sheet on top of any page.
import { mount } from './singles-hub--core--app.js?v=c7c57de095';
import { bindImages, settleImages } from './singles-hub--components--ui.js?v=c7c57de095';
import { initHost, openCard, toast } from './singles-hub--components--host.js?v=c7c57de095';
import { cart, groupLines, openBasket } from './singles-hub--cart.js?v=c7c57de095';
import { api } from './singles-hub--api.js?v=c7c57de095';
import { renderBasket, mountBasketDrawer } from './singles-hub--components--basket.js?v=c7c57de095';
import { bindProductLinkTouch } from './singles-hub--analytics.js?v=c7c57de095';
import { applyRobots } from './singles-hub--seo.js?v=c7c57de095';

const PAGES = {
  hub: () => import('./singles-hub--pages--hub.js?v=c7c57de095').then((m) => m.hubPage),
  builder: () => import('./singles-hub--pages--builder.js?v=c7c57de095').then((m) => m.builderPage),
  search: () => import('./singles-hub--pages--search.js?v=c7c57de095').then((m) => m.searchPage),
  competitive: () => import('./singles-hub--pages--competitive.js?v=c7c57de095').then((m) => m.competitivePage),
  archetype: () => import('./singles-hub--pages--archetype.js?v=c7c57de095').then((m) => m.archetypePage),
  'archetype-hero': () => import('./singles-hub--pages--archetype.js?v=c7c57de095').then((m) => m.archetypeHeroPage),
  list: () => import('./singles-hub--pages--list.js?v=c7c57de095').then((m) => m.listPage),
  explorer: () => import('./singles-hub--pages--explorer.js?v=c7c57de095').then((m) => m.explorerPage),
  'pdp-additions': () => import('./singles-hub--pages--pdp.js?v=c7c57de095').then((m) => m.pdpPage),
  pdp: () => import('./singles-hub--pages--pdp.js?v=c7c57de095').then((m) => m.pdpPage),
  'pdp-specs': () => import('./singles-hub--pages--pdp.js?v=c7c57de095').then((m) => m.pdpSpecsPage),
  requests: () => import('./singles-hub--pages--requests.js?v=c7c57de095').then((m) => m.requestsPage),
};

function pickKind(el) {
  const kind = el.getAttribute('data-mcc-app');
  if (kind !== 'hub') return kind;
  const sp = new URLSearchParams(location.search);
  if (sp.get('build') || sp.get('deck') || sp.get('list') || sp.get('c')) return 'builder';
  if (sp.has('q') || sp.get('screen') === 'search' || ['type', 'set', 'reg', 'rarity', 'price', 'deck_filter'].some((k) => sp.has(k))) return 'search';
  return 'hub';
}

const isEmbedded = (el) => el.getAttribute('data-mcc-embedded') === 'true' || !!(el.closest && el.closest('[data-mcc-embedded="true"]'));
const mounted = [];
async function boot() {
  try { applyRobots(); } catch (e) {}
  bindImages();
  initHost();
  bindProductLinkTouch(() => { const l = cart.last(); return l && l.raw ? l.raw.attributes : null; });
  const els = Array.from(document.querySelectorAll('[data-mcc-app]')).filter((el) => !el.__mcc);
  for (const el of els) {
    el.__mcc = true;
    const kind = pickKind(el);
    const load = PAGES[kind];
    if (!load) { console.warn('[mcc] unknown app', kind); continue; }
    el.classList.add('mcc');
    if (el.classList.contains('page-width') || el.closest('.page-width')) el.classList.add('mcc--contained');
    el.setAttribute('data-mcc-kind', kind);
    // the theme hides server-rendered fallback content once the app is running
    document.querySelectorAll('[data-mcc-fallback]').forEach((f) => { f.hidden = true; });
    try {
      const factory = await load();
      const def = factory(el);
      // Inside the theme the Liquid section owns the SEO h1 + intro; the app must not render a second one.
      def.state = Object.assign({}, def.state, { embedded: isEmbedded(el) });
      const prevRender = def.afterRender;
      def.afterRender = (c) => { settleImages(c.root); if (prevRender) prevRender(c); };
      const comp = mount(el, def);
      mounted.push(comp);
      if (window.ResizeObserver) {
        let t;
        new ResizeObserver(() => { clearTimeout(t); t = setTimeout(() => { const w = el.clientWidth; if (Math.abs((comp.state.w || 0) - w) > 8) comp.setState({ w }); }, 60); }).observe(el);
      }
    } catch (err) {
      console.error('[mcc] failed to start', kind, err);
      document.querySelectorAll('[data-mcc-fallback]').forEach((f) => { f.hidden = false; });
    }
  }
  const card = new URLSearchParams(location.search).get('card');
  if (card) openCard(card);
  cart.get().catch(() => {});
}

// Public hooks for the theme (cart drawer grouping, quick actions)
window.MCC = Object.assign(window.MCC || {}, { cart, api, openCard, toast, openBasket, basket: { groupLines, renderBasket, mountDrawer: mountBasketDrawer }, boot });

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
